import * as React from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { FieldNote } from "@/lib/api";
import { fmtDateTime } from "@/lib/time";

export const MY_FIELD_NOTES_DEFAULT_VISIBLE = 5;
export const MY_FIELD_NOTES_RECENT_DAYS = 30;
export const MY_FIELD_NOTES_FETCH_LIMIT = 50;

type MyFieldNotesListProps = {
  notes: FieldNote[];
  /** Epoch ms for "now" (allows tests / stable offline clock). Defaults to Date.now(). */
  nowMs?: number;
  /** Fetch page size used by the parent query (for cap hint). */
  fetchLimit?: number;
};

function FieldNoteHistoryCard({ note }: { note: FieldNote }) {
  return (
    <div className="rounded-lg border p-3 w-full max-w-full min-w-0">
      <div className="flex flex-wrap items-start justify-between gap-2 min-w-0">
        <div className="flex-1 min-w-0">
          {note.correction_type && (
            <Badge variant="outline" className="mb-1 max-w-full whitespace-normal text-left">
              {note.correction_type}
            </Badge>
          )}
          {note.additional_note ? (
            <p className="text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere] [word-break:break-word] max-w-full min-w-0">
              {note.additional_note}
            </p>
          ) : null}
          {note.reason_for_correction && (
            <p className="mt-1 text-xs text-muted-foreground whitespace-pre-wrap break-words [overflow-wrap:anywhere] max-w-full min-w-0">
              <strong>Reason:</strong> {note.reason_for_correction}
            </p>
          )}
          {(note.original_datetime || note.corrected_datetime) && (
            <div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
              {note.original_datetime && (
                <div>
                  <span className="font-medium text-foreground/70">Original: </span>
                  <span className="whitespace-nowrap">{fmtDateTime(note.original_datetime)}</span>
                </div>
              )}
              {note.corrected_datetime && (
                <div>
                  <span className="font-medium text-foreground/70">Corrected: </span>
                  <span className="whitespace-nowrap">{fmtDateTime(note.corrected_datetime)}</span>
                </div>
              )}
            </div>
          )}
        </div>
        <Badge variant={note.qb_record_id ? "default" : "secondary"} className="shrink-0">
          {note.qb_record_id ? "Synced" : "Pending"}
        </Badge>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Submitted <span className="whitespace-nowrap">{fmtDateTime(note.submitted_at)}</span>
      </p>
    </div>
  );
}

/**
 * Personal field-note history: default last 5 in 30 days, scrollable list,
 * "Show more" for the rest of the 30-day window, and "Show older" beyond that.
 */
export function MyFieldNotesList({
  notes,
  nowMs,
  fetchLimit = MY_FIELD_NOTES_FETCH_LIMIT,
}: MyFieldNotesListProps) {
  const [showAllRecent, setShowAllRecent] = React.useState(false);
  const [showOlder, setShowOlder] = React.useState(false);

  const now = nowMs ?? Date.now();
  const cutoff = now - MY_FIELD_NOTES_RECENT_DAYS * 24 * 60 * 60 * 1000;

  // Parent fetch is submitted_at desc; re-sort defensively.
  const myNotes = React.useMemo(
    () =>
      [...notes].sort(
        (a, b) => new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime(),
      ),
    [notes],
  );

  const recentNotes = React.useMemo(
    () => myNotes.filter((n) => new Date(n.submitted_at).getTime() >= cutoff),
    [myNotes, cutoff],
  );
  const olderNotes = React.useMemo(
    () => myNotes.filter((n) => new Date(n.submitted_at).getTime() < cutoff),
    [myNotes, cutoff],
  );

  // Truth table:
  // !showAllRecent && !showOlder → recent.slice(0, 5)
  //  showAllRecent && !showOlder → recent
  //  showOlder                   → all fetched myNotes
  const visibleNotes = React.useMemo(() => {
    if (showOlder) return myNotes;
    if (showAllRecent) return recentNotes;
    return recentNotes.slice(0, MY_FIELD_NOTES_DEFAULT_VISIBLE);
  }, [showOlder, showAllRecent, myNotes, recentNotes]);

  if (myNotes.length === 0) {
    return <p className="text-sm text-muted-foreground">No field notes submitted yet.</p>;
  }

  const hasRecentOverflow = recentNotes.length > MY_FIELD_NOTES_DEFAULT_VISIBLE;
  const hasOlder = olderNotes.length > 0;
  const onlyOlder = recentNotes.length === 0 && hasOlder;
  const hitFetchCap = myNotes.length >= fetchLimit;

  return (
    <div className="grid gap-3 w-full max-w-full min-w-0">
      {onlyOlder && !showOlder ? (
        <p className="text-sm text-muted-foreground">No field notes in the last 30 days.</p>
      ) : null}

      {visibleNotes.length > 0 ? (
        <div
          className="max-h-[min(60vh,28rem)] space-y-3 overflow-y-auto overscroll-contain pr-1 w-full max-w-full min-w-0"
          tabIndex={0}
          aria-label="Your field notes"
        >
          {visibleNotes.map((note) => (
            <FieldNoteHistoryCard key={note.id} note={note} />
          ))}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        {!showOlder && hasRecentOverflow ? (
          showAllRecent ? (
            <Button type="button" variant="outline" size="sm" onClick={() => setShowAllRecent(false)}>
              Show fewer
            </Button>
          ) : (
            <Button type="button" variant="outline" size="sm" onClick={() => setShowAllRecent(true)}>
              Show all {recentNotes.length} from last 30 days
            </Button>
          )
        ) : null}

        {hasOlder ? (
          showOlder ? (
            <Button type="button" variant="outline" size="sm" onClick={() => setShowOlder(false)}>
              Hide older notes
            </Button>
          ) : (
            <Button type="button" variant="outline" size="sm" onClick={() => setShowOlder(true)}>
              Show {olderNotes.length} older note{olderNotes.length === 1 ? "" : "s"}
            </Button>
          )
        ) : null}
      </div>

      {hitFetchCap && (showAllRecent || showOlder) ? (
        <p className="text-xs text-muted-foreground">
          Showing latest {fetchLimit} notes. Contact support if you need a full archive.
        </p>
      ) : null}
    </div>
  );
}
